'use client';

import type {
	ProductOption,
	ProductVariant,
	ShopifyImage as ShopifyImageType,
} from '@repo/shopify';
import { Skeleton } from '@repo/ui/components/skeleton';
import { ShieldCheck, Truck } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import {
	Fragment,
	type ReactNode,
	Suspense,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useCartActions, useCartSummary } from '@/components/cart/cart-provider';
import { salePercentOff } from '@/components/product/product-badges';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { StarIcon } from '@/components/ui/star-icon';
import type { ApparelBundleOffer } from '@/lib/apparel-bundle-config';
import type { HydrateBundleOffer } from '@/lib/insider-bundle-config';
import { formatMoney, formatPerUnitPrice } from '@/lib/money';
import type { ProductTypeDetail, TrustBadge } from '@/lib/product-page-settings';
import type { ProductPageClientProduct } from '@/lib/product-page-types';
import {
	getDefaultSellingPlan,
	getSellingPlanLabel,
	getSellingPlanPercentOff,
	getSellingPlanUnitPrice,
	getSubscriptionPlans,
} from '@/lib/selling-plans';
import {
	DEFAULT_SUBSCRIBE_SAVE_SETTINGS,
	type SubscribeSaveConfig,
} from '@/lib/subscribe-save-config';
import type { ApparelBundlePick } from './apparel-bundle/apparel-bundle-math';
import { ApparelBundleStrip } from './apparel-bundle/apparel-bundle-strip';
import { useApparelBundleState } from './apparel-bundle/use-apparel-bundle-state';
import { BackInStockNotify } from './back-in-stock-notify';
import type { GiftSelection } from './gift-variant-modal';
import { type BuyBoxMode, BuyBoxModeTabs } from './hydrate-bundle/buy-box-mode-tabs';
import { getTotalSticks } from './hydrate-bundle/hydrate-bundle-math';
import { useHydrateBundleState } from './hydrate-bundle/use-hydrate-bundle-state';
import { LinkedProductColorSelector } from './linked-product-color-selector';
import {
	buildVariantSearchParams,
	getColorOption,
	getDefaultSelectedOptions,
	getSelectedOptionsFromSearchParams,
	type ProductVariantSelectionLike,
	resolveSelectedOptions,
} from './product-option-utils';
import { ProductStyleSelector } from './product-style-selector';
import { ProductSwatchGroup, type SwatchGroupItem } from './product-swatch-group';
import { PurchaseTrustList } from './purchase-trust-list';
import { buildGiftExtraLines, buildGiftSelections } from './subscribe-save-gift-lines';
import { VariantSelector } from './variant-selector';

// Load all subscription controls together for eligible products, including
// controls behind the Crew Pack tab. Switching modes then needs no new chunk.
// SSR stays enabled so those products keep their complete initial buy box.
const SubscriptionProductInfo = dynamic(() =>
	import('./subscription-product-info').then((module) => module.SubscriptionProductInfo),
);

interface LinkedColorProduct {
	handle: string;
	title: string;
	options: ProductOption[];
	variants: ProductVariantSelectionLike[];
	colorValues: string[];
	colorOptionName: string | null;
}

/** The slice of ProductTypeDetail the buy box uses (tagline + fit indicator). */
export type ProductInfoTypeDetails = Pick<ProductTypeDetail, 'tagline' | 'fitScale'>;

export interface ProductInfoProps {
	product: ProductPageClientProduct;
	variants: ProductVariant[];
	/**
	 * True while `variants` availability is cached-only (the Suspense fallback of
	 * the live-availability pass). A cached sold-out claim renders as a disabled
	 * ADD TO CART instead of a definitive SOLD OUT, so stale cache never flashes
	 * the wrong state before live inventory streams in.
	 */
	availabilityPending?: boolean;
	initialSelectedOptions: Record<string, string>;
	siblings?: {
		handle: string;
		title: string;
		productType: string;
		featuredImage: ShopifyImageType | null;
		colorValues: string[];
		images: ShopifyImageType[];
		variants: ProductVariantSelectionLike[];
		discountPercent: number | null;
		keepAcrossColors?: boolean;
	}[];
	linkedColorProducts?: LinkedColorProduct[];
	/**
	 * Print/pattern or flavor swatches sourced from a `swatch-group:*` /
	 * `flavor-group:*` tag (separate products).
	 */
	swatchGroup?: SwatchGroupItem[];
	/** Selector heading — "Design" for apparel prints, "Flavor" for supplements. */
	swatchGroupLabel?: string;
	/**
	 * H1 / sticky-ATC title. Flavor groups use the base product name
	 * (e.g. "Workman Hydrate") while Shopify `product.title` keeps the full
	 * name for cart line items and analytics.
	 */
	displayTitle?: string;
	onSelectedColorChange?: (color: string | null) => void;
	enableReviews?: boolean;
	moneyLocale?: string;
	/** Star rating from Klaviyo Reviews API */
	rating?: number | null;
	/** Total review count from Klaviyo Reviews API */
	reviewCount?: number | null;
	/** Optional server-streamed review summary for first paint */
	reviewSummarySlot?: ReactNode;
	/** Server-rendered CMS sizing chart (SizingChartContent); null hides the link. */
	sizingChartSlot?: ReactNode;
	/** CMS product type details — only the fields the buy box renders. */
	typeDetails?: ProductInfoTypeDetails | null;
	/** CMS-driven Subscribe & Save labels/benefits, resolved for this product */
	subscribeSave?: SubscribeSaveConfig | null;
	/** Crew Pack offer (config + flavors + kit product); null hides the bundle tab. */
	bundle?: HydrateBundleOffer | null;
	/** Apparel Bundle & Save ladder (cart-driven); null hides the strip. */
	apparelBundle?: ApparelBundleOffer | null;
	/** CMS-driven trust badges for this product type; null falls back to the built-in pair */
	trustBadges?: TrustBadge[] | null;
	/** Featured review to display as social proof */
	featuredReview?: {
		author: string;
		content: string;
		rating: number;
		verified: boolean;
	} | null;
	/** Optional server-streamed featured review for first paint */
	featuredReviewSlot?: ReactNode;
}

interface PendingRequestedSelection {
	searchParamsString: string;
	selectedOptions: Record<string, string>;
}

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Shown under the CTA when the CMS has no trust badges configured. */
const DEFAULT_TRUST_BADGES: TrustBadge[] = [
	{ label: 'Printed in America', icon: 'flag' },
	{ label: '30-Day Returns', icon: 'clock' },
];

function TrustBadgeIcon({ icon }: { icon: string }) {
	switch (icon) {
		case 'clock':
			return (
				<svg
					className="h-4 w-4"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<circle cx="12" cy="12" r="10" />
					<polyline points="12 6 12 12 16 14" />
				</svg>
			);
		case 'shield':
			return <ShieldCheck className="h-4 w-4" aria-hidden="true" />;
		case 'truck':
			return <Truck className="h-4 w-4" aria-hidden="true" />;
		case 'star':
			return <StarIcon className="h-4 w-4" aria-hidden="true" />;
		default:
			return (
				<svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
					<rect x="2" y="4" width="20" height="16" stroke="currentColor" strokeWidth="2" />
					<path d="M2 8h20 M2 12h20 M2 16h20" stroke="currentColor" strokeWidth="2" />
					<rect x="2" y="4" width="10" height="8" fill="currentColor" />
				</svg>
			);
	}
}

function StarRating({ rating, count }: { rating: number; count: number }) {
	return (
		// A scroll-to-section control, not a navigation: the previous router
		// <Link href="#reviews"> resolved to the current URL and prefetched the
		// PDP itself (App Shell on view, the full product on hover).
		<button
			type="button"
			className="flex items-center gap-2 transition-opacity hover:opacity-70"
			onClick={() => {
				document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' });
				window.history.replaceState(window.history.state, '', '#reviews');
			}}
		>
			<div className="flex gap-0.5">
				{[1, 2, 3, 4, 5].map((n) => (
					<StarIcon
						key={`rating-star-${n}`}
						className={`h-4 w-4 ${
							n <= Math.round(rating)
								? 'fill-neutral-900 text-neutral-900'
								: 'fill-neutral-200 text-neutral-200'
						}`}
					/>
				))}
			</div>
			<span className="text-sm text-neutral-600">
				{rating} ({count} Reviews)
			</span>
		</button>
	);
}

export function ProductInfoSkeleton() {
	return (
		<div className="flex flex-col">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="flex gap-0.5">
						{[1, 2, 3, 4, 5].map((n) => (
							<Skeleton key={`rating-star-${n}`} className="h-4 w-4 rounded-sm" />
						))}
					</div>
					<Skeleton className="h-4 w-24" />
				</div>
				<Skeleton className="h-6 w-12" />
			</div>
			<Skeleton className="mt-2 h-8 w-3/4 lg:h-9" />
			<Skeleton className="mt-2 h-7 w-24" />
			<div className="mt-5 space-y-4">
				<div>
					<Skeleton className="h-4 w-12" />
					<div className="mt-3 flex flex-wrap gap-3">
						{[1, 2, 3].map((n) => (
							<div key={`style-${n}`} className="w-16 md:w-[4.5rem]">
								<Skeleton className="h-14 w-full rounded-sm md:h-16" />
								<Skeleton className="mt-px h-5 w-full rounded-none" />
							</div>
						))}
					</div>
				</div>
				<div>
					<Skeleton className="h-4 w-28" />
					<div className="mt-2 flex gap-2">
						{[1, 2, 3].map((n) => (
							<Skeleton key={`color-${n}`} className="h-8 w-8 rounded-full" />
						))}
					</div>
				</div>
				<div>
					<Skeleton className="h-4 w-24" />
					<div className="mt-2 grid grid-cols-4 gap-1.5">
						{[1, 2, 3, 4].map((n) => (
							<Skeleton key={`size-${n}`} className="h-11 w-full rounded" />
						))}
					</div>
				</div>
				<div className="rounded-md border border-neutral-200 px-2.5 py-2">
					<div className="flex items-center justify-between">
						<Skeleton className="h-3 w-8" />
						<Skeleton className="h-3 w-24" />
					</div>
					<div className="mt-2.5 flex items-center justify-between">
						<Skeleton className="h-2.5 w-16" />
						<Skeleton className="h-2.5 w-16" />
					</div>
					<Skeleton className="mt-2 h-px w-full rounded-full" />
				</div>
				<div className="space-y-3">
					<Skeleton className="h-12 w-full" />
					<div className="flex items-center justify-center gap-3">
						<div className="flex items-center gap-1.5">
							<Skeleton className="h-5 w-5" />
							<Skeleton className="h-3 w-24" />
						</div>
						<Skeleton className="h-3 w-2" />
						<div className="flex items-center gap-1.5">
							<Skeleton className="h-4 w-4" />
							<Skeleton className="h-3 w-20" />
						</div>
					</div>
				</div>
				<div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5">
					<div className="flex justify-center gap-0.5">
						{[1, 2, 3, 4, 5].map((n) => (
							<Skeleton key={`featured-star-${n}`} className="h-3.5 w-3.5 rounded-sm" />
						))}
					</div>
					<Skeleton className="mx-auto mt-1.5 h-4 w-full max-w-[20rem]" />
					<Skeleton className="mx-auto mt-1 h-4 w-4/5" />
					<div className="mt-1.5 flex items-center justify-center gap-2">
						<Skeleton className="h-4 w-20" />
						<Skeleton className="h-4 w-24" />
					</div>
				</div>
			</div>
		</div>
	);
}

function PriceWithCents({
	amount,
	currencyCode,
	locale = 'en-US',
	className,
}: {
	amount: string;
	currencyCode: string;
	locale?: string;
	className?: string;
}) {
	const num = Number.parseFloat(amount);
	const parts = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currencyCode,
	}).formatToParts(num);

	return (
		<span className={className}>
			{parts.map((part) =>
				part.type === 'decimal' ? null : part.type === 'fraction' ? (
					<sup key={`${part.type}-${part.value}`} className="text-[0.6em] align-super ml-0.5">
						{part.value}
					</sup>
				) : (
					<span key={`${part.type}-${part.value}`}>{part.value}</span>
				),
			)}
		</span>
	);
}

/** Optimistic line info + add options for the buy-box add-to-cart call. */
function buildAddToCartPayload({
	product,
	selectedVariant,
	selectedSellingPlan,
	giftLines,
}: {
	product: ProductPageClientProduct;
	selectedVariant: ProductVariant;
	selectedSellingPlan: ReturnType<typeof getSubscriptionPlans>[number] | null;
	giftLines: ReturnType<typeof buildGiftExtraLines>;
}) {
	return {
		productInfo: {
			productId: product.id,
			title: product.title,
			handle: product.handle,
			vendor: product.vendor,
			productType: product.productType,
			image: selectedVariant.image || product.featuredImage,
			variantTitle: selectedVariant.title === 'Default Title' ? undefined : selectedVariant.title,
			sku: selectedVariant.sku,
			price: selectedSellingPlan
				? getSellingPlanUnitPrice(selectedVariant.price, selectedSellingPlan)
				: selectedVariant.price,
		},
		options: {
			analyticsSource: 'product_page',
			// name = derived label so the optimistic drawer line shows the real
			// frequency (Recharge plan names can be wrong, see getSellingPlanLabel).
			sellingPlan: selectedSellingPlan
				? { id: selectedSellingPlan.id, name: getSellingPlanLabel(selectedSellingPlan) }
				: undefined,
			extraLines: giftLines.length > 0 ? giftLines : undefined,
		},
	};
}

function variantMatchesSelectedOptions(
	variant: Pick<ProductVariant, 'selectedOptions'>,
	selectedOptions: Record<string, string>,
) {
	return variant.selectedOptions.every((option) => selectedOptions[option.name] === option.value);
}

function isProductPathForHandle(pathname: string, handle: string) {
	return pathname.replace(/\/$/, '').endsWith(`/products/${handle}`);
}

function deleteOptionSearchParams(params: URLSearchParams) {
	for (const key of Array.from(params.keys()).filter((key) => key.startsWith('option.'))) {
		params.delete(key);
	}
}

function hasSelectionSearchParams(params: URLSearchParams) {
	if (params.has('variant')) return true;

	for (const key of params.keys()) {
		if (key.startsWith('option.')) return true;
	}

	return false;
}

function shouldWaitForRequestedSelection(
	variant: Pick<ProductVariant, 'selectedOptions'>,
	pendingSelection: PendingRequestedSelection | null,
	searchParamsString: string,
) {
	return (
		pendingSelection?.searchParamsString === searchParamsString &&
		!variantMatchesSelectedOptions(variant, pendingSelection.selectedOptions)
	);
}

function getCurrentBrowserSearchParams(fallbackSearchParamsString: string) {
	if (typeof window === 'undefined') {
		return new URLSearchParams(fallbackSearchParamsString);
	}

	return new URLSearchParams(window.location.search);
}

function replaceBrowserUrlWithoutNavigation(nextUrl: string) {
	// Use the native prototype method so selection-only URL sync does not notify
	// patched history listeners that treat `?variant=` as a page navigation.
	const replaceState =
		typeof History !== 'undefined' ? History.prototype.replaceState : window.history.replaceState;

	replaceState.call(window.history, window.history.state, '', nextUrl);
}

function getVariantUrlSyncTarget({
	pathname,
	searchParamsString,
	selectedVariant,
	syncVariantUrl,
}: {
	pathname: string;
	searchParamsString: string;
	selectedVariant: ProductVariant;
	syncVariantUrl: boolean;
}) {
	const browserParams = getCurrentBrowserSearchParams(searchParamsString);
	if (!syncVariantUrl && !hasSelectionSearchParams(browserParams)) return null;

	const variantId = buildVariantSearchParams(selectedVariant).get('variant');
	if (!variantId) return null;

	const currentParams = new URLSearchParams(browserParams);
	deleteOptionSearchParams(currentParams);

	const nextParams = new URLSearchParams(browserParams);
	deleteOptionSearchParams(nextParams);
	nextParams.set('variant', variantId);

	const query = nextParams.toString();
	if (currentParams.toString() === query) return null;

	return {
		query,
		url: query ? `${pathname}?${query}` : pathname,
	};
}

function resolveOptionsAfterOptionChange(
	variants: ProductVariant[],
	currentOptions: Record<string, string>,
	name: string,
	value: string,
) {
	const nextRequestedOptions = { ...currentOptions, [name]: value };
	const resolvedOptions = resolveSelectedOptions(variants, nextRequestedOptions);

	if (name.toLowerCase() !== 'color' || resolvedOptions[name] === value) {
		return resolvedOptions;
	}

	return resolveSelectedOptions(variants, { [name]: value });
}

function FitIndicator({
	typeDetails,
	sizingChartSlot,
}: {
	typeDetails?: ProductInfoTypeDetails | null;
	sizingChartSlot?: ReactNode;
}) {
	if (!typeDetails?.fitScale) return null;

	const position = Math.min(5, Math.max(1, typeDetails.fitScale));
	const hasSizingChart = !!sizingChartSlot;

	return (
		<div className="rounded-md border border-neutral-200 px-2.5 py-2">
			<div className="flex items-center justify-between">
				<p className="text-[11px] font-semibold text-neutral-900">Size</p>
				{hasSizingChart && <SizingChartDialog>{sizingChartSlot}</SizingChartDialog>}
			</div>
			<div className="mt-2.5 flex items-center justify-between text-[10px] leading-none text-neutral-500">
				<span>Fits too small</span>
				<span>Fits too large</span>
			</div>
			<div className="relative mt-1">
				<div className="absolute left-0 right-0 top-1/2 z-0 h-px -translate-y-1/2 bg-neutral-300" />
				<div className="relative z-10 flex items-center justify-between">
					{[1, 2, 3, 4, 5].map((n) => (
						<div
							key={`fit-${n}`}
							className={`h-2 w-2 rounded-sm ${
								n === position ? 'bg-neutral-900' : 'border border-neutral-300 bg-white'
							}`}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function FeaturedReview({
	review,
}: {
	review?: { author: string; content: string; rating: number; verified: boolean } | null;
}) {
	if (!review) return null;

	return (
		<div className="rounded border border-neutral-200 bg-neutral-50 p-4">
			<div className="flex justify-center gap-0.5">
				{[1, 2, 3, 4, 5].map((n) => (
					<StarIcon
						key={`review-star-${n}`}
						className={`h-3.5 w-3.5 ${
							n <= Math.round(review.rating)
								? 'fill-brand-accent text-brand-accent'
								: 'fill-neutral-200 text-neutral-200'
						}`}
					/>
				))}
			</div>
			<p className="mt-1.5 text-center text-[13px] leading-snug text-neutral-700">
				&ldquo;{review.content}&rdquo;
			</p>
			<div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[13px]">
				<span className="font-semibold text-neutral-900">{review.author}</span>
				{review.verified && (
					<span className="flex items-center gap-1 text-neutral-600">
						<svg
							className="h-3.5 w-3.5 flex-shrink-0 text-neutral-900"
							viewBox="0 0 22 22"
							aria-hidden="true"
						>
							<path
								d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.691-.13.635-.08 1.293.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.604-.274 1.262-.144 1.897.13.636.433 1.221.878 1.69.47.446 1.055.749 1.69.879.635.13 1.294.084 1.902-.139.272.587.702 1.087 1.24 1.443s1.167.554 1.813.571c.646-.017 1.272-.215 1.812-.571s.97-.856 1.245-1.443c.604.223 1.262.27 1.897.139.634-.13 1.218-.433 1.687-.879.445-.469.75-1.054.882-1.69.132-.635.083-1.293-.14-1.897.586-.273 1.084-.705 1.439-1.246.355-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"
								fill="currentColor"
							/>
						</svg>
						Verified Buyer
					</span>
				)}
			</div>
		</div>
	);
}

function SizingChartDialog({ children }: { children: ReactNode }) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	const handleOpen = useCallback(() => {
		dialogRef.current?.showModal();
	}, []);

	const handleClose = useCallback(() => {
		dialogRef.current?.close();
	}, []);

	return (
		<>
			<button
				type="button"
				className="text-[11px] font-medium text-neutral-500 underline underline-offset-2 transition-colors hover:text-neutral-900"
				onClick={handleOpen}
			>
				View Sizing Chart
			</button>

			{/* biome-ignore lint/a11y/useKeyWithClickEvents: dialog handles Escape natively */}
			<dialog
				ref={dialogRef}
				className="m-auto w-[calc(100%-1rem)] max-w-2xl rounded border-0 p-0 shadow-2xl backdrop:bg-black/60 sm:w-full"
				onClick={(e) => {
					if (e.target === dialogRef.current) handleClose();
				}}
			>
				<div className="relative">
					{/* Header */}
					<div className="flex items-center justify-between border-b border-neutral-200 px-4 py-2">
						<h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900">
							Sizing Chart
						</h2>
						<button
							type="button"
							className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
							onClick={handleClose}
							aria-label="Close sizing chart"
						>
							<svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
								<path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
							</svg>
						</button>
					</div>

					{children}
				</div>
			</dialog>
		</>
	);
}

export function ProductInfo(props: ProductInfoProps) {
	return props.bundle || getSubscriptionPlans(props.product).length > 0 ? (
		<SubscriptionProductInfo {...props} />
	) : (
		<ProductInfoBase {...props} />
	);
}

interface SubscriptionComponents {
	HydrateBundlePanel: typeof import('./hydrate-bundle/hydrate-bundle-panel').HydrateBundlePanel;
	PurchaseOptionSelector: typeof import('./purchase-option-selector').PurchaseOptionSelector;
	GiftVariantModal: typeof import('./gift-variant-modal').GiftVariantModal;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: product detail state combines variant selection, pricing, and add-to-cart flows
export function ProductInfoBase({
	product,
	variants,
	availabilityPending = false,
	initialSelectedOptions,
	siblings = [],
	linkedColorProducts = [],
	swatchGroup = [],
	swatchGroupLabel = 'Design',
	displayTitle,
	onSelectedColorChange,
	enableReviews = false,
	moneyLocale,
	rating: ratingProp,
	reviewCount: reviewCountProp,
	reviewSummarySlot,
	sizingChartSlot,
	typeDetails,
	subscribeSave,
	bundle,
	apparelBundle,
	trustBadges,
	featuredReview,
	featuredReviewSlot,
	subscriptionComponents,
}: ProductInfoProps & { subscriptionComponents?: SubscriptionComponents }) {
	const HydrateBundlePanel = subscriptionComponents?.HydrateBundlePanel;
	const PurchaseOptionSelector = subscriptionComponents?.PurchaseOptionSelector;
	const GiftVariantModal = subscriptionComponents?.GiftVariantModal;
	const { addItem, getCart } = useCartActions();
	const { isOpen: isCartOpen } = useCartSummary();
	const [selectedOptions, setSelectedOptions] =
		useState<Record<string, string>>(initialSelectedOptions);
	const [_addedToCart, setAddedToCart] = useState(false);
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const [showStickyATC, setShowStickyATC] = useState(false);
	const [shouldSyncVariantUrl, setShouldSyncVariantUrl] = useState(false);
	// Sold-out option (e.g. a sold-out size) the shopper tapped to open the Back in
	// Stock prompt. Kept separate from `selectedOptions` so it never reaches the
	// purchase path or URL sync (both of which deliberately drop sold-out variants).
	const [notifySelection, setNotifySelection] = useState<{ name: string; value: string } | null>(
		null,
	);
	// Multi-variant gifts awaiting option selection after a subscription add.
	const [giftSelections, setGiftSelections] = useState<GiftSelection[]>([]);
	// Insider Bundle vs single box. One box is the default everywhere — the
	// shopper opts into the bundle tab — so reloads and flavor-swatch
	// navigations never flash or reset into bundle mode. Bundle purchase state
	// is lifted here so the headline price row mirrors it while the tab is
	// active.
	const [buyBoxMode, setBuyBoxMode] = useState<BuyBoxMode>('single');
	const isBundleMode = !!bundle && buyBoxMode === 'bundle';
	const bundleState = useHydrateBundleState(
		bundle,
		subscribeSave ?? DEFAULT_SUBSCRIBE_SAVE_SETTINGS,
	);
	// Subscribe & Save — recurring plans synced from the subscription app. When
	// non-null, add-to-cart attaches the line to this selling plan.
	const subscriptionPlans = useMemo(() => getSubscriptionPlans(product), [product]);
	const subscribeSaveConfig = subscribeSave ?? DEFAULT_SUBSCRIBE_SAVE_SETTINGS;
	// Subscription-only products keep the selector even when the CMS disables it —
	// Shopify rejects a one-time add for them.
	const showSubscribeSave =
		subscriptionPlans.length > 0 && (subscribeSaveConfig.enabled || !!product.requiresSellingPlan);
	// Subscribe is pre-selected whenever it's offered, defaulting to the
	// 30-day plan when Recharge carries several frequencies.
	const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<string | null>(() => {
		const plans = getSubscriptionPlans(product);
		if (plans.length === 0) return null;
		const offered = subscribeSave?.enabled !== false || product.requiresSellingPlan;
		return offered ? (getDefaultSellingPlan(plans)?.id ?? null) : null;
	});
	const selectedSellingPlan = showSubscribeSave
		? (subscriptionPlans.find((plan) => plan.id === selectedSellingPlanId) ?? null)
		: null;
	const atcRef = useRef<HTMLDivElement>(null);
	const sizeSelectRef = useRef<HTMLDivElement>(null);
	// Set once the shopper (or the URL sync) explicitly picks options; an
	// explicit choice must survive the live-availability merge below.
	const hasExplicitSelectionRef = useRef(false);
	const hasSizeOption = product.options.some(
		(opt) => opt.name.toLowerCase() === 'size' && opt.values.length > 1,
	);
	const [hasUserSelectedSize, setHasUserSelectedSize] = useState(() => !hasSizeOption);
	const currentColorOption = getColorOption(product.options);
	const showLinkedColorSelector =
		!!currentColorOption &&
		linkedColorProducts.some((linkedProduct) => linkedProduct.colorValues.length > 0);

	// Notify parent of selected color changes for image filtering
	const selectedColor = currentColorOption
		? (selectedOptions[currentColorOption.name] ?? null)
		: null;
	useEffect(() => {
		onSelectedColorChange?.(selectedColor);
	}, [selectedColor, onSelectedColorChange]);

	const resolvedVariants = variants;
	const selectedVariant = useMemo(() => {
		return resolvedVariants.find((variant) =>
			variant.selectedOptions.every((opt) => selectedOptions[opt.name] === opt.value),
		);
	}, [resolvedVariants, selectedOptions]);

	// The exact (sold-out) variant the shopper tapped — matched by the chosen value
	// plus current selections, ignoring availability (the resolver excludes it).
	const notifyVariant = useMemo(() => {
		if (!notifySelection) return null;
		const { name, value } = notifySelection;
		return (
			resolvedVariants.find((variant) =>
				variant.selectedOptions.every((opt) =>
					opt.name === name ? opt.value === value : selectedOptions[opt.name] === opt.value,
				),
			) ?? null
		);
	}, [notifySelection, resolvedVariants, selectedOptions]);

	// Once live availability lands, an untouched default selection that turned
	// out to be sold out is re-resolved to the first live-available variant —
	// the same correction the server used to apply in its second render pass.
	useEffect(() => {
		if (availabilityPending || hasExplicitSelectionRef.current) return;
		setSelectedOptions((prev) => {
			const current = resolvedVariants.find((variant) =>
				variant.selectedOptions.every((opt) => prev[opt.name] === opt.value),
			);
			if (current?.availableForSale) return prev;
			const defaults = getDefaultSelectedOptions(resolvedVariants);
			return Object.keys(defaults).length > 0 ? defaults : prev;
		});
	}, [availabilityPending, resolvedVariants]);

	const hasAvailableVariants = resolvedVariants.some((variant) => variant.availableForSale);
	const isAvailable = selectedVariant?.availableForSale ?? false;
	// Treat an active notify selection as sold-out so the buy box shows SOLD OUT
	// rather than offering to add the previously selected in-stock size.
	const showSoldOutState =
		!hasAvailableVariants || !selectedVariant || !isAvailable || notifySelection !== null;
	// A cached sold-out claim while live availability is still streaming — render
	// a disabled ADD TO CART instead of SOLD OUT; the live pass settles the state.
	const soldOutPending = availabilityPending && showSoldOutState && notifySelection === null;
	const price = selectedVariant?.price || product.priceRange.minVariantPrice;
	const compareAtPrice = selectedVariant?.compareAtPrice;
	const isOnSale = Boolean(
		compareAtPrice && Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(price.amount),
	);
	const salePercent = salePercentOff(price.amount, compareAtPrice?.amount);
	// Slot one of the bundle is always this PDP's current selection — adding it
	// to the cart is what moves the ladder.
	const apparelCurrentPick = useMemo<ApparelBundlePick | null>(() => {
		if (!apparelBundle || !selectedVariant) return null;
		const unitPrice = Number(selectedVariant.price.amount);
		const unitCompareAt = selectedVariant.compareAtPrice
			? Number(selectedVariant.compareAtPrice.amount)
			: null;
		return {
			handle: product.handle,
			productId: product.id,
			title: product.title,
			productType: product.productType,
			merchandiseId: selectedVariant.id,
			variantTitle: selectedVariant.title ?? null,
			sku: selectedVariant.sku,
			vendor: product.vendor,
			image: selectedVariant.image ?? product.featuredImage ?? null,
			price: Number.isFinite(unitPrice) ? unitPrice : 0,
			compareAt: unitCompareAt !== null && Number.isFinite(unitCompareAt) ? unitCompareAt : null,
			currencyCode: selectedVariant.price.currencyCode,
			isCurrent: true,
		};
	}, [apparelBundle, selectedVariant, product]);
	const apparelBundleState = useApparelBundleState(
		apparelBundle,
		apparelCurrentPick,
		price.currencyCode,
	);

	// With a subscription plan selected, the headline price is the per-delivery
	// plan price and the one-time price becomes the strikethrough reference.
	const planPercentOff = selectedSellingPlan
		? getSellingPlanPercentOff(price, selectedSellingPlan)
		: null;
	const displayPrice = selectedSellingPlan
		? getSellingPlanUnitPrice(price, selectedSellingPlan)
		: price;
	const displayComparePrice = selectedSellingPlan
		? planPercentOff !== null
			? price
			: null
		: isOnSale && compareAtPrice
			? compareAtPrice
			: null;
	// While the bundle tab is active, the headline price mirrors the bundle
	// totals, struck through against the full four-bag retail value ($180).
	const bundlePricing = isBundleMode ? bundleState.pricing : null;
	const bundleSubscribeHeadline = !!bundlePricing?.subscribeTotal && bundleState.subscribeSelected;
	const headlinePrice = bundlePricing
		? bundleSubscribeHeadline && bundlePricing.subscribeTotal
			? bundlePricing.subscribeTotal
			: bundlePricing.oneTimeTotal
		: displayPrice;
	const headlineComparePrice = bundlePricing
		? Number.parseFloat(bundlePricing.retailTotal.amount) > Number.parseFloat(headlinePrice.amount)
			? bundlePricing.retailTotal
			: null
		: displayComparePrice;
	const headlineSavePercent = bundlePricing
		? bundleSubscribeHeadline
			? bundlePricing.subscribePercentOff
			: null
		: planPercentOff;
	// Per-stick tab meta follows the current subscribe/one-time selection like
	// the headline price does ($25 subscription on a 32ct bag reads $0.78 /
	// stick, not the $50 one-time $1.56). The one-bag side rides displayPrice;
	// the kit side uses the selected frequency's plan price in bundle mode and
	// the kit's default plan while the one-bag subscription is selected.
	const kitSubscribing = bundlePricing
		? bundleState.subscribeSelected
		: Boolean(selectedSellingPlan);
	const kitDefaultPlan = bundle ? getDefaultSellingPlan(bundle.kitProduct.sellingPlans) : null;
	const kitTabPrice = bundle
		? bundlePricing?.subscribeTotal && bundleState.subscribeSelected
			? bundlePricing.subscribeTotal
			: kitSubscribing && kitDefaultPlan
				? getSellingPlanUnitPrice(bundle.kitProduct.price, kitDefaultPlan)
				: bundle.kitProduct.price
		: null;
	// One box ↔ Insider Bundle switch, rendered directly above the purchase
	// options in both modes.
	const modeTabs = bundle ? (
		<BuyBoxModeTabs
			mode={buyBoxMode}
			onChange={setBuyBoxMode}
			oneBoxLabel={bundle.config.oneBoxTabLabel}
			oneBoxMeta={`(${bundle.config.sticksPerBox}ct) · ${formatPerUnitPrice(displayPrice, bundle.config.sticksPerBox, moneyLocale)} / stick`}
			bundleLabel={bundle.config.bundleTabLabel}
			bundleMeta={`(${getTotalSticks(bundle.config)}ct) · ${formatPerUnitPrice(
				kitTabPrice ?? bundle.kitProduct.price,
				getTotalSticks(bundle.config),
				moneyLocale,
			)} / stick`}
			bestValueBadge={bundle.config.bestValueBadge}
			bundleBagCount={bundle.config.boxCount + bundle.config.bonusCount}
		/>
	) : null;
	const ctaLabel = selectedSellingPlan
		? (
				subscribeSaveConfig.subscribeCtaLabel || DEFAULT_SUBSCRIBE_SAVE_SETTINGS.subscribeCtaLabel
			).toUpperCase()
		: 'ADD TO CART';
	// Thumbnail inside the subscription plan row (buy-box mockup plan thumb).
	const planThumbImage = selectedVariant?.image || product.featuredImage;
	// CMS microcopy under the trust line, with {price}/{oneTimePrice} resolved
	// against the selected (or first) plan.
	const subtextPlan = selectedSellingPlan ?? getDefaultSellingPlan(subscriptionPlans);
	const trustSubtext =
		showSubscribeSave && subtextPlan
			? subscribeSaveConfig.microcopy
					.replaceAll(
						'{price}',
						formatMoney(getSellingPlanUnitPrice(price, subtextPlan), moneyLocale),
					)
					.replaceAll('{oneTimePrice}', formatMoney(price, moneyLocale))
			: null;

	// Show sticky mobile ATC bar when the main button scrolls out of view,
	// but hide it once the footer is in view so it doesn't cover the footer
	useEffect(() => {
		const el = atcRef.current;
		if (!el) return;

		const footer = document.querySelector('footer');
		let atcAboveViewport = false;
		let footerVisible = false;
		const update = () => setShowStickyATC(atcAboveViewport && !footerVisible);

		const atcObserver = new IntersectionObserver(
			([entry]) => {
				if (entry) {
					// Only show sticky ATC when the button has scrolled above the viewport,
					// not when the user is above it and hasn't scrolled down to it yet
					atcAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
					update();
				}
			},
			{ threshold: 0 },
		);
		atcObserver.observe(el);

		let footerObserver: IntersectionObserver | undefined;
		if (footer) {
			footerObserver = new IntersectionObserver(
				([entry]) => {
					if (entry) {
						footerVisible = entry.isIntersecting;
						update();
					}
				},
				{ threshold: 0 },
			);
			footerObserver.observe(footer);
		}

		return () => {
			atcObserver.disconnect();
			footerObserver?.disconnect();
		};
	}, []);

	const handleOptionChange = useCallback(
		(name: string, value: string) => {
			hasExplicitSelectionRef.current = true;
			// Choosing any in-stock option dismisses the back-in-stock prompt.
			setNotifySelection(null);
			if (name.toLowerCase() === 'size') {
				setHasUserSelectedSize(true);
			}
			setShouldSyncVariantUrl(true);
			setSelectedOptions((prev) => {
				return resolveOptionsAfterOptionChange(resolvedVariants, prev, name, value);
			});
		},
		[resolvedVariants],
	);

	const handleSelectSoldOut = useCallback((name: string, value: string) => {
		hasExplicitSelectionRef.current = true;
		if (name.toLowerCase() === 'size') {
			setHasUserSelectedSize(true);
		}
		setNotifySelection({ name, value });
	}, []);

	const handleRequestedSelection = useCallback((requestedOptions: Record<string, string>) => {
		hasExplicitSelectionRef.current = true;
		setSelectedOptions((prev) => {
			const keys = Object.keys(requestedOptions);
			if (
				keys.length === Object.keys(prev).length &&
				keys.every((key) => prev[key] === requestedOptions[key])
			) {
				return prev;
			}
			return requestedOptions;
		});
		setHasUserSelectedSize(true);
	}, []);

	const handleAddToCart = async () => {
		if (!selectedVariant || !isAvailable || isAddingToCart) return;

		// Gifts come with the ORDER, not with every subscription: only the first
		// subscription line in the cart brings its gifts along.
		const cart = getCart();
		const cartHasSubscription = !!cart?.lines.edges.some((edge) => edge.node.sellingPlanAllocation);
		const giftsApply = !!selectedSellingPlan && !cartHasSubscription;

		const payload = buildAddToCartPayload({
			product,
			selectedVariant,
			selectedSellingPlan,
			giftLines: giftsApply
				? buildGiftExtraLines(subscribeSaveConfig.gifts, cart, price.currencyCode)
				: [],
		});

		setIsAddingToCart(true);
		try {
			const nextCart = await addItem(selectedVariant.id, 1, payload.productInfo, payload.options);

			if (!nextCart) return;

			if (giftsApply) {
				setGiftSelections(buildGiftSelections(subscribeSaveConfig.gifts, nextCart));
			}
			setAddedToCart(true);
			setTimeout(() => setAddedToCart(false), 2000);
		} finally {
			setIsAddingToCart(false);
		}
	};

	return (
		<>
			{GiftVariantModal && (
				<GiftVariantModal
					gifts={giftSelections}
					open={giftSelections.length > 0}
					onOpenChange={(open) => {
						if (!open) setGiftSelections([]);
					}}
				/>
			)}
			<Suspense fallback={null}>
				<ProductVariantSearchParamsSync
					productHandle={product.handle}
					options={product.options}
					variants={resolvedVariants}
					selectedVariant={selectedVariant}
					syncVariantUrl={shouldSyncVariantUrl}
					onRequestedSelection={handleRequestedSelection}
				/>
			</Suspense>
			<div className="flex flex-col">
				{/* Star Rating + Sale badge */}
				<div className="flex items-center justify-between">
					{reviewSummarySlot ??
						(enableReviews && ratingProp && (
							<StarRating rating={ratingProp} count={reviewCountProp ?? 0} />
						))}
					{isOnSale && (
						<span className="rounded bg-brand-accent px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
							{salePercent ? `Sale · ${salePercent}%` : 'Sale'}
						</span>
					)}
				</div>

				{/* Title */}
				<h1 className="mt-2 text-[28px] font-extrabold uppercase leading-none tracking-[-0.02em] lg:text-[31px]">
					{displayTitle ?? product.title}
				</h1>

				{/* Price — superscript cents; reflects the selected subscription plan,
				    or the bundle totals while the Insider Bundle tab is active */}
				<div className="mt-2 flex items-end gap-2.5">
					<PriceWithCents
						amount={headlinePrice.amount}
						currencyCode={headlinePrice.currencyCode}
						locale={moneyLocale}
						className="text-[22px] font-extrabold leading-none tracking-[-0.02em] text-neutral-900"
					/>
					{headlineComparePrice && (
						<span className="pb-px text-sm leading-none text-neutral-500 line-through">
							{formatMoney(headlineComparePrice, moneyLocale)}
						</span>
					)}
					{headlineSavePercent !== null && (
						<span className="mb-0.5 rounded-sm bg-brand-accent px-[7px] py-1 text-[11px] font-bold uppercase leading-none tracking-wider text-white">
							Save {headlineSavePercent}%
						</span>
					)}
				</div>

				{/* CMS tagline under the price (per product type) */}
				{typeDetails?.tagline && (
					<p className="mt-3 text-sm leading-relaxed text-neutral-600">{typeDetails.tagline}</p>
				)}

				{isBundleMode && bundle && HydrateBundlePanel && (
					<div className="mt-6">
						<HydrateBundlePanel
							state={bundleState}
							subscribeSaveConfig={subscribeSaveConfig}
							moneyLocale={moneyLocale}
							onGiftSelections={setGiftSelections}
							modeTabs={modeTabs}
						/>
						<div className="mt-5">
							{featuredReviewSlot ?? <FeaturedReview review={featuredReview} />}
						</div>
					</div>
				)}

				{!isBundleMode && (
					<div className="mt-6 space-y-5">
						{/* Style Selector (sibling products) */}
						{siblings.length > 1 && (
							<ProductStyleSelector
								currentHandle={product.handle}
								siblings={siblings}
								selectedOptions={selectedOptions}
							/>
						)}

						{/* One bag ↔ Crew Pack switch sits above the flavor swatches so the
						    format is chosen before the flavor */}
						{modeTabs}

						{/* Print/pattern or flavor swatches (separate products, not Shopify options) */}
						{swatchGroup.length > 1 && (
							<ProductSwatchGroup items={swatchGroup} groupLabel={swatchGroupLabel} />
						)}

						{showLinkedColorSelector && (
							<LinkedProductColorSelector
								currentHandle={product.handle}
								options={product.options}
								variants={resolvedVariants}
								linkedProducts={linkedColorProducts}
								selectedOptions={selectedOptions}
								onColorChange={handleOptionChange}
							/>
						)}

						{/* Variant Selector */}
						{product.options.length > 0 &&
							!(product.options.length === 1 && product.options[0]?.values.length === 1) && (
								<div ref={sizeSelectRef}>
									<VariantSelector
										options={product.options}
										selectedOptions={selectedOptions}
										variants={resolvedVariants}
										onOptionChange={handleOptionChange}
										hiddenOptionNames={
											showLinkedColorSelector && currentColorOption ? [currentColorOption.name] : []
										}
										soldOutSelection={notifySelection}
										onSelectSoldOut={handleSelectSoldOut}
									/>
								</div>
							)}

						{/* Fit indicator */}
						<FitIndicator typeDetails={typeDetails} sizingChartSlot={sizingChartSlot} />

						{/* Subscribe & Save — only for products with recurring selling plans */}
						{showSubscribeSave && PurchaseOptionSelector && (
							<PurchaseOptionSelector
								plans={subscriptionPlans}
								price={price}
								selectedPlanId={selectedSellingPlanId}
								onSelect={setSelectedSellingPlanId}
								requiresSellingPlan={product.requiresSellingPlan}
								thumbnail={
									planThumbImage
										? { url: planThumbImage.url, alt: planThumbImage.altText || product.title }
										: null
								}
								config={subscribeSaveConfig}
								moneyLocale={moneyLocale}
							/>
						)}

						{/* Add to Cart — burnt-orange CTA */}
						<div ref={atcRef} className="space-y-3">
							<div>
								<button
									type="button"
									className={`w-full rounded py-4 text-base font-bold uppercase tracking-[0.08em] transition-all ${
										!showSoldOutState || soldOutPending
											? 'bg-brand-accent text-white hover:brightness-110'
											: 'cursor-not-allowed bg-neutral-300 text-neutral-500'
									}`}
									onClick={handleAddToCart}
									disabled={showSoldOutState || isAddingToCart}
								>
									{!showSoldOutState ? (
										isAddingToCart ? (
											<span className="flex items-center justify-center">
												<svg
													className="h-5 w-5 animate-spin text-white"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
													aria-hidden="true"
												>
													<title>Loading</title>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													/>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													/>
												</svg>
											</span>
										) : (
											ctaLabel
										)
									) : soldOutPending ? (
										ctaLabel
									) : (
										'SOLD OUT'
									)}
								</button>
							</div>

							{/* Bundle ladder — a quiet strip under the CTA that mirrors the
							    cart. Adding this product is what moves it. */}
							{apparelBundle && !showSoldOutState && (
								<ApparelBundleStrip state={apparelBundleState} moneyLocale={moneyLocale} />
							)}

							{/* Back in stock — notify-me button under the SOLD OUT button */}
							{notifyVariant && !notifyVariant.availableForSale && (
								<BackInStockNotify
									key={notifyVariant.id}
									variant={notifyVariant}
									productTitle={product.title}
								/>
							)}

							{/* Under-CTA trust: subscription products get the gifts/guarantee/reviews
						    checklist here; everything else shows its badges under the review. */}
							{showSubscribeSave && (
								<PurchaseTrustList
									items={subscribeSaveConfig.benefits.map((benefit) => benefit.text)}
									subtext={trustSubtext}
								/>
							)}
						</div>

						{/* Featured review testimonial */}
						{featuredReviewSlot ?? <FeaturedReview review={featuredReview} />}

						{/* Trust badges — CMS-driven per product type, falling back to the
						    built-in pair. Under the review so the bundle strip sits directly
						    against the CTA it extends. */}
						{!showSubscribeSave && (
							<div className="flex items-center justify-center gap-3 text-xs font-semibold uppercase tracking-wider text-neutral-700">
								{(trustBadges?.length ? trustBadges : DEFAULT_TRUST_BADGES).map((badge, index) => (
									<Fragment key={badge.label}>
										{index > 0 && <span className="text-neutral-300">+</span>}
										<span className="flex items-center gap-1.5">
											<TrustBadgeIcon icon={badge.icon} />
											{badge.label}
										</span>
									</Fragment>
								))}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Sticky mobile ATC bar — compact single row mirroring the buy-box price treatment.
			    Hidden in bundle mode: the bundle CTA manages its own state. */}
			{showStickyATC && !isCartOpen && !isBundleMode && (
				<div className="sticky-atc-bar fixed bottom-0 left-0 right-0 z-50 border-t border-neutral-200 bg-white px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden">
					<div className="flex items-center gap-3">
						{planThumbImage && (
							<ShopifyImage
								src={planThumbImage.url}
								alt={planThumbImage.altText || product.title}
								width={44}
								height={44}
								sizes="44px"
								className="h-11 w-11 shrink-0 rounded-md bg-neutral-100 object-cover"
							/>
						)}
						<div className="min-w-0 flex-1">
							<p className="truncate text-[13px] font-bold uppercase leading-tight tracking-wide">
								{displayTitle ?? product.title}
							</p>
							<div className="mt-1 flex items-center gap-1.5">
								<PriceWithCents
									amount={displayPrice.amount}
									currencyCode={displayPrice.currencyCode}
									locale={moneyLocale}
									className="text-[15px] font-extrabold leading-none tracking-[-0.02em] text-neutral-900"
								/>
								{displayComparePrice && (
									<span className="text-xs leading-none text-neutral-400 line-through">
										{formatMoney(displayComparePrice, moneyLocale)}
									</span>
								)}
								{planPercentOff !== null && (
									<span className="rounded-sm bg-brand-accent px-1.5 py-[3px] text-[10px] font-bold uppercase leading-none tracking-wider text-white">
										Save {planPercentOff}%
									</span>
								)}
							</div>
						</div>
						<button
							type="button"
							className={`shrink-0 whitespace-nowrap rounded-md px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.08em] transition-all ${
								!hasUserSelectedSize || !showSoldOutState || soldOutPending
									? 'bg-brand-accent text-white hover:brightness-110'
									: 'cursor-not-allowed bg-neutral-300 text-neutral-500'
							}`}
							onClick={
								!hasUserSelectedSize
									? () =>
											sizeSelectRef.current?.scrollIntoView({
												behavior: 'smooth',
												block: 'center',
											})
									: handleAddToCart
							}
							disabled={hasUserSelectedSize && (showSoldOutState || isAddingToCart)}
						>
							{!hasUserSelectedSize ? (
								'SELECT SIZE'
							) : !showSoldOutState ? (
								isAddingToCart ? (
									<svg
										className="mx-auto h-[18px] w-[18px] animate-spin text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										aria-hidden="true"
									>
										<title>Loading</title>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
								) : (
									ctaLabel
								)
							) : soldOutPending ? (
								ctaLabel
							) : (
								'SOLD OUT'
							)}
						</button>
					</div>
				</div>
			)}
		</>
	);
}

function ProductVariantSearchParamsSync({
	productHandle,
	options,
	variants,
	selectedVariant,
	syncVariantUrl,
	onRequestedSelection,
}: {
	productHandle: string;
	options: ProductOption[];
	variants: ProductVariant[];
	selectedVariant?: ProductVariant;
	syncVariantUrl: boolean;
	onRequestedSelection: (requestedOptions: Record<string, string>) => void;
}) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const searchParamsString = searchParams.toString();
	const productHandleRef = useRef(productHandle);
	const lastAppliedSearchParamsRef = useRef<string | null>(null);
	const lastWrittenSearchParamsRef = useRef<string | null>(null);
	const pendingRequestedSelectionRef = useRef<PendingRequestedSelection | null>(null);

	if (productHandleRef.current !== productHandle) {
		productHandleRef.current = productHandle;
		lastAppliedSearchParamsRef.current = null;
		lastWrittenSearchParamsRef.current = null;
		pendingRequestedSelectionRef.current = null;
	}

	useBrowserLayoutEffect(() => {
		if (lastWrittenSearchParamsRef.current === searchParamsString) return;
		if (lastAppliedSearchParamsRef.current === searchParamsString) return;
		lastAppliedSearchParamsRef.current = searchParamsString;

		const requestedSelectedOptions = getSelectedOptionsFromSearchParams(
			new URLSearchParams(searchParamsString),
			variants,
			options,
		);

		if (Object.keys(requestedSelectedOptions).length === 0) return;
		pendingRequestedSelectionRef.current = {
			searchParamsString,
			selectedOptions: requestedSelectedOptions,
		};
		onRequestedSelection(requestedSelectedOptions);
	}, [onRequestedSelection, options, searchParamsString, variants]);

	useEffect(() => {
		if (!selectedVariant) return;
		if (!isProductPathForHandle(pathname, productHandle)) return;

		const pendingRequestedSelection = pendingRequestedSelectionRef.current;
		if (
			shouldWaitForRequestedSelection(
				selectedVariant,
				pendingRequestedSelection,
				searchParamsString,
			)
		) {
			return;
		}
		if (pendingRequestedSelection?.searchParamsString === searchParamsString) {
			pendingRequestedSelectionRef.current = null;
		}

		const syncTarget = getVariantUrlSyncTarget({
			pathname,
			searchParamsString,
			selectedVariant,
			syncVariantUrl,
		});
		if (!syncTarget) return;

		lastWrittenSearchParamsRef.current = syncTarget.query;
		replaceBrowserUrlWithoutNavigation(syncTarget.url);
	}, [pathname, productHandle, searchParamsString, selectedVariant, syncVariantUrl]);

	return null;
}
